import type { SVGProps } from 'react';

// Detailed stamp icons. Single-color line + accent fill art that
// inherits `currentColor` so the parent <Stamp> controls earned/locked tint.
export type IconProps = SVGProps<SVGSVGElement>;

const base = (props: IconProps) => ({
  viewBox: '0 0 64 64',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  ...props,
});

const dot = { fill: 'currentColor', stroke: 'none' } as const;

export const LavashIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <ellipse cx="32" cy="34" rx="22" ry="13" />
    <ellipse cx="32" cy="32" rx="22" ry="13" />
    <path d="M16 30c5-4 11-5 16-3M30 27c6-1 12 0 17 4M20 36c5 3 10 4 15 3M32 39c5 0 10-2 14-5" />
    <path d="M24 25c2 5 3 9 2 14M40 25c-2 5-3 9-2 14M32 24v18" opacity=".7" />
    <circle cx="22" cy="30" r="1" {...dot} />
    <circle cx="42" cy="34" r="1" {...dot} />
    <circle cx="32" cy="37" r="1" {...dot} />
  </svg>
);

export const GataIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="32" cy="32" r="19" />
    <circle cx="32" cy="32" r="13" />
    <path d="M32 13v6M32 45v6M13 32h6M45 32h6M19 19l4 4M45 19l-4 4M19 45l4-4M45 45l-4-4" />
    <path d="M27 27l10 10M37 27 27 37" opacity=".6" />
    <circle cx="32" cy="32" r="3" {...dot} />
  </svg>
);

export const BeerIcon = (p: IconProps) => (
  <svg {...base(p)}>
    {/* Foam head */}
    <path d="M20 20c-2-4 2-7 5-6 1-4 7-4 8 0 4-2 8 1 7 5 3 1 3 6-1 6H22c-4 0-5-4-2-5Z" />
    {/* Mug body */}
    <path d="M20 25h22v26a4 4 0 0 1-4 4H24a4 4 0 0 1-4-4Z" />
    {/* Handle */}
    <path d="M42 30h5a5 5 0 0 1 5 5v6a5 5 0 0 1-5 5h-5" />
    {/* Brew level + bubbles */}
    <path d="M20 33h22" opacity=".6" />
    <circle cx="27" cy="42" r="1.4" {...dot} />
    <circle cx="34" cy="46" r="1.4" {...dot} />
    <circle cx="31" cy="38" r="1.2" {...dot} />
  </svg>
);

export const DolmaIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M20 18c-7 5-9 16-5 27 3 8 16 10 23 4 7-7 7-23 0-32-5-7-13-4-18 1Z" />
    <path d="M24 21c5 7 9 16 9 27M38 19c-3 9-3 18 2 28" opacity=".75" />
    <path d="M27 26c3 4 6 9 7 14M36 24c-2 5-2 11 1 17" opacity=".5" />
    <path d="M24 16c4-3 9-3 13 0" />
    <circle cx="32" cy="46" r="1.4" {...dot} />
  </svg>
);

export const CoffeeMountainIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="m10 42 11-15 7 9 8-13 13 19Z" />
    <path d="m21 27 3 4h-6Z" {...dot} />
    <path d="M44 42c1-2 4-2 5 0" opacity=".6" />
    <rect x="20" y="44" width="20" height="11" rx="2" />
    <path d="M40 46h4a3 3 0 0 1 0 6h-4" />
    <path d="M26 38c-1-2 1-3 0-5M32 38c-1-2 1-3 0-5" />
    <path d="M18 55h24" />
  </svg>
);

export const HorseIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M18 52V36c0-9 6-15 15-15h3l7-9 1 9c5 2 7 7 7 13v18" />
    <path d="M44 21c3-2 5-2 7 0-2 2-4 3-7 3" />
    <path d="M16 52h6M44 52h6M19 26l-5-5M22 24l-4-7" />
    <path d="M33 24c3 4 5 9 5 15" opacity=".6" />
    <circle cx="42" cy="18" r="1.4" {...dot} />
    <path d="M24 34c5-3 12-3 18 0" opacity=".5" />
  </svg>
);

export const SkewerIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M10 12 54 56" />
    <path d="M8 14h4M52 54h4" />
    <circle cx="22" cy="24" r="5.5" />
    <circle cx="32" cy="34" r="5.5" />
    <circle cx="42" cy="44" r="5.5" />
    <path d="M19 21l6 6M29 31l6 6M39 41l6 6" opacity=".5" />
    <path d="M12 40c4 6 10 6 14 0M22 50c4 6 10 6 14 0" opacity=".6" />
  </svg>
);

export const WineIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M19 13h26c0 16-7 25-13 25S19 29 19 13Z" />
    <path d="M21 20h22" opacity=".7" />
    <path d="M24 14c1 8 4 14 8 18 4-4 7-10 8-18" opacity=".4" />
    <path d="M32 38v13M22 51h20" />
    <circle cx="32" cy="26" r="1.4" {...dot} />
  </svg>
);

export const MountainIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="m6 52 15-26 9 13 7-11 21 24Z" />
    <path d="m21 26 5 7H16Z" {...dot} />
    <path d="m44 28 6 9H38Z" {...dot} />
    <path d="M14 44c4 3 9 3 13 0M34 41c4 3 9 3 13 0" opacity=".4" />
    <circle cx="48" cy="16" r="4" />
    <path d="M48 12v-3M48 23v-3M52 16h3M41 16h3" opacity=".6" />
  </svg>
);

export const RopewayIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M6 14 58 26" />
    <path d="M10 12v6M54 22v6" />
    <path d="M26 21v4m-7 0h16l4 17H22Z" />
    <path d="M23 30h18" opacity=".6" />
    <path d="M27 30v12M35 30v12" opacity=".5" />
    <path d="M22 42c2 4 16 4 18 0" opacity=".4" />
  </svg>
);

export const FishIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M12 32c9-13 27-13 36 0-9 13-27 13-36 0Z" />
    <path d="M48 23c5 2 8 6 9 9-1 3-4 7-9 9" />
    <path d="M20 26c5-4 12-4 17 0M20 38c5 4 12 4 17 0" opacity=".4" />
    <path d="M30 24c2 5 2 11 0 16" opacity=".5" />
    <circle cx="22" cy="30" r="2" />
    <circle cx="22" cy="30" r="0.8" {...dot} />
    <path d="M14 32c3-1 5-1 8 0" opacity=".5" />
  </svg>
);

export const QuillIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M50 12c-14 2-28 17-32 33l9-3c15-5 23-17 23-30Z" />
    <path d="M44 18c-9 4-17 12-21 24" opacity=".6" />
    <path d="m22 42 22-22" opacity=".4" />
    <path d="M18 45c-3 3-4 6-5 9 4-1 7-2 10-5" />
    <path d="M10 54h14" />
    <circle cx="45" cy="16" r="1.3" {...dot} />
  </svg>
);

export const DonutIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="32" cy="32" r="19" />
    <circle cx="32" cy="32" r="6.5" />
    <path d="M14 28c4-9 14-13 23-11M50 36c-4 9-14 13-23 11" opacity=".55" />
    <path d="M24 18l3 5M40 19l-2 5M48 26l-5 2M16 38l5-1M44 44l-3-4M22 46l2-5M33 13l1 5M51 32l-5 1" {...dot} />
  </svg>
);

export const SoupIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M10 28h44c-1 14-10 23-22 23S11 42 10 28Z" />
    <path d="M14 32h36" opacity=".6" />
    <path d="M22 36c4 4 16 4 20 0" opacity=".4" />
    <path d="M25 22c-1-3 3-4 2-8M32 22c-1-3 3-4 2-8M39 22c-1-3 3-4 2-8" />
    <path d="M7 28h50" />
  </svg>
);

export const BrandyIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M27 10h10v4l1 6c4 2 6 6 6 11v18a4 4 0 0 1-4 4H24a4 4 0 0 1-4-4V31c0-5 2-9 6-11l1-6Z" />
    <rect x="22" y="34" width="20" height="14" rx="1" />
    <path d="M26 38h12M26 42h12" opacity=".5" />
    <path d="M27 14h10M27 17h10" opacity=".6" />
    <path d="M29 8h6" />
  </svg>
);

export const CandyIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="32" cy="32" r="11" />
    <path d="M21 32 9 23v18ZM43 32l12-9v18Z" />
    <path d="m12 26 6 4M12 38l6-4M52 26l-6 4M52 38l-6-4" opacity=".5" />
    <path d="M27 27c3-2 7-2 10 2M27 37c3 2 7 2 10-2" opacity=".5" />
    <circle cx="32" cy="32" r="2.5" {...dot} />
  </svg>
);

export const HotelIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M10 50V26l22-13 22 13v24Z" />
    <path d="M32 13v-4" />
    <path d="m6 30 26-15 26 15" opacity=".5" />
    <rect x="25" y="38" width="14" height="12" />
    <path d="M32 38v12" opacity=".5" />
    <rect x="18" y="30" width="6" height="6" />
    <rect x="40" y="30" width="6" height="6" />
    <circle cx="36" cy="44" r="1" {...dot} />
  </svg>
);

export const MeatDishIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <ellipse cx="32" cy="40" rx="24" ry="9" />
    <ellipse cx="32" cy="38" rx="24" ry="9" />
    <path d="M20 35c3-9 21-9 24 0" />
    <path d="M24 33c2-4 14-4 16 0" opacity=".5" />
    <path d="M14 24c0-3 4-3 4 0s-4 3-4 6M46 24c0-3 4-3 4 0s-4 3-4 6" opacity=".5" />
    <circle cx="28" cy="31" r="1" {...dot} />
    <circle cx="36" cy="32" r="1" {...dot} />
  </svg>
);

export const HerbBreadIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M14 40c0-13 8-20 18-20s18 7 18 20Z" />
    <path d="M14 40h36v6a3 3 0 0 1-3 3H17a3 3 0 0 1-3-3Z" />
    <path d="M26 25c2 5 2 10 4 15M38 23c-1 5-1 10-3 15M32 22v18" opacity=".55" />
    <path d="M22 30c2 1 3 3 3 6M42 30c-2 1-3 3-3 6" opacity=".4" />
  </svg>
);

export const ZiplineIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M6 13 58 27" />
    <path d="M9 11v5M55 23v5" />
    <path d="M28 19v4m-5 0h10l-2 7h-6Z" />
    <path d="M30 30v7" />
    <circle cx="30" cy="42" r="5" />
    <path d="M25 47c2 4 8 4 10 0M27 56l3-7 3 7" opacity=".6" />
  </svg>
);

export const TentIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M32 11 9 52h46Z" />
    <path d="M32 11v41" />
    <path d="m25 52 7-14 7 14Z" />
    <path d="M32 11 20 52M32 11l12 41" opacity=".4" />
    <path d="M9 52h46" />
    <path d="M14 49c0-3 4-3 4-6" opacity=".5" />
  </svg>
);

export const CornIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M32 10c9 5 11 18 7 31-2 7-12 11-14 0-4-13-2-26 7-31Z" />
    <path d="M29 18v26M35 18v26" opacity=".5" />
    <path d="M26 24h12M26 31h12M26 38h11" opacity=".45" />
    <path d="M36 22c5-5 10-5 13 0-2 7-7 9-12 7M28 22c-5-5-10-5-13 0 2 7 7 9 12 7" />
    <circle cx="32" cy="28" r="1" {...dot} />
  </svg>
);

export const CheeseIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="m10 44 38-23 9 15-33 17Z" />
    <path d="m10 44 38-23M48 21l-4 17" opacity=".55" />
    <circle cx="26" cy="38" r="2.5" />
    <circle cx="37" cy="33" r="2" />
    <circle cx="44" cy="29" r="1.6" />
    <circle cx="31" cy="44" r="1.4" />
  </svg>
);

export const KhachkarIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M21 14h22v36H21z" />
    <path d="M24 17h16v30H24z" opacity=".5" />
    <path d="M32 21v22M26 28h12M28 24h8M28 40h8" />
    <path d="M27 47c0-5 10-5 10 0" />
    <path d="M30 26c-2 2-2 4 0 6M34 26c2 2 2 4 0 6" opacity=".5" />
  </svg>
);

export const DressIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M25 12h14l-4 9 10 31H19l10-31Z" />
    <path d="M27 12c0 5 10 5 10 0" />
    <path d="M23 32h18M22 40h20" opacity=".5" />
    <path d="M32 21v31" opacity=".4" />
    <path d="M27 26c2 3 8 3 10 0" opacity=".45" />
    <circle cx="32" cy="36" r="1.2" {...dot} />
    <circle cx="32" cy="44" r="1.2" {...dot} />
  </svg>
);

export const CarpetIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <rect x="13" y="15" width="38" height="34" rx="1.5" />
    <rect x="19" y="21" width="26" height="22" />
    <path d="M32 21v22M19 32h26" opacity=".6" />
    <path d="M26 28l6 4-6 4M38 28l-6 4 6 4" opacity=".55" />
    <path d="M13 15v-3m9 3v-3m10 3v-3m10 3v-3m9 3v-3M13 49v3m9-3v3m10-3v3m10-3v3m9-3v3" />
  </svg>
);

// Armenian dried fruits — a bowl of dried apricots, figs and prunes.
export const DriedFruitsIcon = (p: IconProps) => (
  <svg {...base(p)}>
    {/* Bowl */}
    <path d="M14 34h36c0 9-8 15-18 15S14 43 14 34Z" />
    <path d="M18 40c4 3 24 3 28 0" opacity=".5" />
    {/* Dried fruit pieces mounded above the rim */}
    <path d="M24 34c-3-1-4-4-2-6 2-2 6-1 6 2 0 2-2 4-4 4Z" />
    <path d="M33 33c-3 0-5-3-3-6 2-2 6-2 7 1 1 3-1 5-4 5Z" />
    <path d="M42 34c-3 0-5-2-4-5 1-2 5-3 6 0 1 2 0 5-2 5Z" />
    {/* Pits / seed dots + a stem */}
    <circle cx="27" cy="31" r="1" {...dot} />
    <circle cx="35" cy="30" r="1" {...dot} />
    <circle cx="43" cy="31" r="1" {...dot} />
    <path d="M35 27c1-2 3-3 5-3" opacity=".6" />
  </svg>
);

// Generic category fallbacks
export const FoodIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="32" cy="32" r="18" />
    <circle cx="32" cy="32" r="12" opacity=".5" />
    <path d="M22 27c3 7 17 7 20 0M26 38h12" />
    <path d="M18 16v8a3 3 0 0 0 6 0v-8M21 16v10" />
    <path d="M44 16c-3 1-4 4-4 8s2 5 4 6v6" />
  </svg>
);

export const WineryIcon = WineIcon;

export const ActivityIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="32" cy="17" r="6" />
    <path d="M32 23v15M21 31l11-5 11 5M22 52l10-14 10 14" />
    <path d="M27 38h10" opacity=".5" />
  </svg>
);

export const DefaultIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="32" cy="32" r="18" />
    <circle cx="32" cy="32" r="11" opacity=".5" />
    <path d="M32 19v26M19 32h26M23 23l18 18M41 23 23 41" opacity=".6" />
    <circle cx="32" cy="32" r="3" {...dot} />
  </svg>
);
